import React, { useMemo, useState } from "react";
import moment from "moment";
import sectionStyles from "./UnassignedEventsSection.module.scss";

const DEFAULT_VISIBLE_PER_DAY = 1;
const caretIcon = require("assets/images/caret-587E85.svg").default;

function UnassignedSeeMoreButton({ t, expanded, canExpand, onToggle }) {
  if (!canExpand) return null;

  return (
    <div
      className={sectionStyles["unassigned-see-more"]}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <span>
        {expanded ? t("scheduler.agenda.seeLess") : t("scheduler.agenda.seeMore")}
      </span>
      <img
        className={
          expanded ? sectionStyles["unassigned-see-more-caret-expanded"] : ""
        }
        src={caretIcon}
        alt=""
      />
    </div>
  );
}

/**
 * Collapsible unassigned section for daily and weekly calendar layouts.
 */
export function UnassignedEventsCalendarSection({
  t,
  daysData = [],
  renderEventCard,
  collapsedVisiblePerDay = DEFAULT_VISIBLE_PER_DAY,
  defaultExpanded = false,
  labelColumnClassName = "",
  dayCellClassName = "",
  onCellDragOver,
  onCellDrop,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const totalCount = useMemo(
    () => daysData.reduce((sum, day) => sum + (day.events?.length || 0), 0),
    [daysData],
  );

  const canExpand = daysData.some(
    (day) => (day.events?.length || 0) > collapsedVisiblePerDay,
  );

  const getVisibleEvents = (events = []) => {
    if (!events.length) return [];
    if (expanded) return events;
    return events.slice(0, collapsedVisiblePerDay);
  };

  return (
    <div className={sectionStyles["unassigned-row"]}>
      <div
        className={`${sectionStyles["unassigned-label-column"]} ${labelColumnClassName}`}
      >
        <span className={sectionStyles["unassigned-count"]}>
          {t("scheduler.unassignedCount", { count: totalCount })}
        </span>
        <UnassignedSeeMoreButton
          t={t}
          expanded={expanded}
          canExpand={canExpand}
          onToggle={() => setExpanded((prev) => !prev)}
        />
      </div>

      {daysData.map((day) => (
        <div
          key={day.dateKey || day.date}
          className={`${sectionStyles["unassigned-day-cell"]} ${dayCellClassName}`}
          onDragOver={
            onCellDragOver
              ? (e) => onCellDragOver(e, day)
              : undefined
          }
          onDrop={onCellDrop ? (e) => onCellDrop(e, day) : undefined}
        >
          {getVisibleEvents(day.events).map((event) =>
            renderEventCard(event, day.date, day.dateKey, day),
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Collapsible unassigned section for the daily layout (single day column).
 */
export function UnassignedEventsDailySection({
  t,
  events = [],
  renderEventCard,
  dayMoment,
  collapsedVisiblePerDay = DEFAULT_VISIBLE_PER_DAY,
  defaultExpanded = false,
  onCellDragOver,
  onCellDrop,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const totalCount = events.length;
  const canExpand = totalCount > collapsedVisiblePerDay;
  const visibleEvents = expanded
    ? events
    : events.slice(0, collapsedVisiblePerDay);

  return (
    <div className={sectionStyles["unassigned-row"]}>
      <div className={sectionStyles["unassigned-label-column"]}>
        <span className={sectionStyles["unassigned-count"]}>
          {t("scheduler.unassignedCount", { count: totalCount })}
        </span>
        <UnassignedSeeMoreButton
          t={t}
          expanded={expanded}
          canExpand={canExpand}
          onToggle={() => setExpanded((prev) => !prev)}
        />
      </div>
      <div
        className={sectionStyles["unassigned-daily-content"]}
        onDragOver={onCellDragOver}
        onDrop={onCellDrop}
      >
        {visibleEvents.map((event) => renderEventCard(event, dayMoment))}
      </div>
    </div>
  );
}

/**
 * Table row for weekly staff-name layout.
 */
export function UnassignedEventsStaffRow({
  t,
  daysData = [],
  renderEventCard,
  collapsedVisiblePerDay = DEFAULT_VISIBLE_PER_DAY,
  defaultExpanded = false,
  labelCellClassName = "",
  labelCellStyle,
  onCellDragOver,
  onCellDrop,
  dragPreviewCell = null,
  renderDragPreview,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const totalCount = useMemo(
    () => daysData.reduce((sum, day) => sum + (day.events?.length || 0), 0),
    [daysData],
  );

  const canExpand = daysData.some(
    (day) => (day.events?.length || 0) > collapsedVisiblePerDay,
  );

  const getVisibleEvents = (events = []) => {
    if (!events.length) return [];
    if (expanded) return events;
    return events.slice(0, collapsedVisiblePerDay);
  };

  return (
    <tr>
      <th
        className={`${sectionStyles["unassigned-staff-row-label"]} ${labelCellClassName}`}
        style={labelCellStyle}
      >
        <div className={sectionStyles["unassigned-staff-count"]}>
          {t("scheduler.unassignedShiftsCount", { count: totalCount })}
        </div>
        <UnassignedSeeMoreButton
          t={t}
          expanded={expanded}
          canExpand={canExpand}
          onToggle={() => setExpanded((prev) => !prev)}
        />
      </th>
      {daysData.map((day) => {
        const dayDateKey =
          day.dateStr ||
          (day.date ? moment(day.date).format("YYYY-MM-DD") : "");
        const showDropPreview =
          dragPreviewCell?.isUnassignedRow &&
          dragPreviewCell?.requestedDate === dayDateKey;

        return (
        <td
          key={day.dateKey || day.date}
          className={sectionStyles["unassigned-staff-row-cell"]}
          onDragOver={
            onCellDragOver
              ? (e) => onCellDragOver(e, day)
              : undefined
          }
          onDrop={onCellDrop ? (e) => onCellDrop(e, day) : undefined}
        >
          <div className={sectionStyles["unassigned-staff-events"]}>
            {getVisibleEvents(day.events).map((event) =>
              renderEventCard(event, day.date, day.dateKey, day),
            )}
            {showDropPreview && renderDragPreview?.(day)}
          </div>
        </td>
        );
      })}
    </tr>
  );
}
